<template>
  <div class="relative">
    <base-scroll
      ref="scrollContainerRef"
      dir="vertical"
      :style="`height: calc(100dvh - ${pxToRem(header.height.full)}`"
    >
      <section
        v-for="({
          yearTitle,
          title,
          titleWidth,
          isDecadeTitle,
          picture,
          copy,
          ctaText,
          quote,
          quoteName,
          overflowHidden,
          picturePosition,
          layout,
          isBlackAndWhiteText,
          bwTransition,
          isWhiteCopy,
          isBlackCopy,
        }, key) in sections"
        :key
        ref="itemRefs"
        class="h-full"
      >
        <div :class="getFlexContainerClasses(layout)">
          <div
            v-style="{
              'max-w': {
                sm: '32.5rem',
                md: 'none',
              },
              'w': {
                lg: layout.leftSplit ? '39%' : null,
              },
            }"
            class="m-5 relative z-1"
            :class="getFlexItemOneClasses(layout)"
          >
            <h2
              class="fw-medium mb-2"
              :class="[
                isDecadeTitle ? 'text-4xl' : 'text-2xl',
                isBlackCopy ? 'c-black' : 'c-white',
                { 'c-white md:c-black': isBlackAndWhiteText && bwTransition !== 'lg' },
                { 'c-white lg:c-black': isBlackAndWhiteText && bwTransition === 'lg' },
              ]"
              :style="{ maxWidth: pxToRem(titleWidth) }"
            >
              <span
                v-if="isDecadeTitle"
                v-style:text-size="{ sm: '1.25rem', lg: '1.75rem' }"
                v-text="yearTitle"
                class="mb-1 block title-1"
              />
              <span
                v-else
                v-style:text-size="{ sm: '1rem', md: '1.375rem' }"
                v-text="yearTitle"
                class="mb-1 block title-2"
              />

              <span
                v-if="isDecadeTitle"
                v-style:text-size="{ sm: '2.25rem', lg: '5rem' }"
                v-html="title"
                class="lg:lh-12"
              />
              <span
                v-else
                v-style:text-size="{ sm: '1.5rem', md: '2rem', lg: '3.75rem' }"
                v-html="title"
                class="lg:lh-10"
              />
            </h2>
            <blockquote
              v-if="quote"
              class="text-lg c-white fw-light lh-normal pl-8 max-w-xs relative lg:text-4xl lg:pl-10 md:pl-7 md:max-w-xl"
            >
              <div>
                <span
                  class="lh-tight absolute-0"
                  :class="[isFr ? '-mt-2 sm:-mt-5' : 'sm:-mt-3 mt-3']"
                  style="font-size: 3.5rem"
                >
                  {{ openingQuote }}
                </span>
                {{ quote }}
                <span
                  class="lh-0 relative lg:pl-2"
                  :class="{ 'mt-5': isFr }"
                  style="font-size: 3.5rem; bottom: -7px"
                >
                  {{ closingQuote }}
                </span>
              </div>
              <p class="text-sm mt-5 lg:mt-10">
                {{ quoteName }}
              </p>
            </blockquote>
            <p
              v-if="copy"
              v-html="copy"
              class="text-sm lh-normal lg:text-md md:text-base"
              :class="{
                'c-white md:c-black': isBlackAndWhiteText && bwTransition !== 'lg',
                'c-white lg:c-black': isBlackAndWhiteText && bwTransition === 'lg',
                'c-white': isWhiteCopy,
                'c-black': isBlackCopy,
              }"
              style="max-width: 36.5rem"
            />
            <vf-button
              v-if="ctaText"
              class="text-sm mt-8"
              to="/about-us/our-story/remembering-cofounder-douglas-tompkins"
              variant="primary-inverse"
            >
              {{ ctaText }}
            </vf-button>
          </div>
          <div
            :class="[
              { 'overflow-hidden': overflowHidden },
              getFlexItemTwoClasses(layout),
            ]"
          >
            <base-picture
              v-bind="picture"
              class="full"
              fit="cover"
              :position="picturePosition"
              :props-img="{ class: { '<md:object-top': picturePosition } }"
            />
          </div>
        </div>
      </section>
    </base-scroll>
    <div
      v-style="{
        bgv: {
          sm: 'linear-gradient(0,#000 20%,rgba(0,0,0,0) 40%)',
          md: 'linear-gradient(0,#000 5%,rgba(0,0,0,0) 60%)',
        },
        h: { sm: '15.5rem', md: '19rem' },
        op: { sm: '.5', md: '.7' },
      }"
      class="w-full bottom-0 absolute"
    />
    <base-scroll
      class-wrapper="bottom-16 lg:bottom-22 mx-a z-2 h-0"
      style-container="scroll-snap-type: none"
    >
      <div class="m-a h-15 w-5xl relative lg:max-w-6xl lg:w-full">
        <ol class="flex w-full">
          <li
            v-for="({ activeYears, decadeYear, text }, key) in nav"
            :key
            class="c-white p-1 text-center h-full w-38 md:w-full"
          >
            <base-button
              :id="`scroll-nav-${decadeYear}`"
              class="group c-grey-70 flex w-full center"
              tabindex="-1"
              @click="scrollContainerRef?.scrollToIndex(decadeYear)"
            >
              <div class="f-col h-full center duration">
                <span
                  class="mb-3 b b-white rounded-lg bg-black block relative z-1 group-hover:bg-white group-hover:scale-150"
                  :class="{ 'bg-white': activeYears.includes(scrollContainerRef?.activePage) }"
                  style="transition: transform .6s cubic-bezier(.16,1,.3,1); width: 0.625rem; height: 0.625rem"
                />
                <span
                  class="lg:title-3 title-4"
                  :class="{ 'c-white': activeYears.includes(scrollContainerRef?.activePage) }"
                >
                  {{ text }}
                </span>
              </div>
            </base-button>
          </li>
        </ol>
        <div
          class="m-a mt-2 bg-transparent top-0 absolute overflow-hidden"
          role="presentation"
          style="left:7.5%; height:3px; max-width:64rem; width: 87%"
        >
          <div
            ref="progressRef"
            class="bg-white h-full"
          />
        </div>
      </div>
    </base-scroll>
  </div>
</template>

<script lang="ts" setup>
import type { BaseScroll as BaseScrollType } from '#components'
import { getLanguageCode } from '#core/utils/i18n'
import { useElementBounding } from '@vueuse/core'
import { interpolate } from 'popmotion'

definePageMeta({ header: { transparent: false } })

const { $locale } = useNuxtApp()
const language = getLanguageCode($locale).toLowerCase()
const progressRef = ref()

const rescaleProgress = interpolate([0, 61], [0, 1])
const { apply } = useMotion(progressRef, {
  initial: {
    'scaleX': 0,
    'transform-origin': 'left',
  }
})

const isFr = language === 'fr'

const { data, error } = await useApi().internal.getStaticPage('about-us/history')

if (!data.value) throw createError({ ...error.value!, fatal: true })

const { openingQuote, closingQuote, sections, nav } = data.value

const getFlexContainerClasses = (layout) => ({
  'relative h-full flex': true,
  '<lg:pb-20': !layout.mobile?.alignStart,
  '<lg:pb-8': layout.mobile?.alignStart,
  '<md:items-center': layout.mobile?.alignCenter,
  '<md:items-end': layout.mobile?.alignEnd,
  '<md:flex-col': !layout.mobile?.imgAbsolute,
  'md:items-center': layout.desktop?.alignCenter,
  'md:justify-right': layout.desktop?.justifyRight,
  'md:items-start': layout.desktop?.alignStart
})

const getFlexItemOneClasses = (layout) => ({
  'lg:-mt-40': !layout.desktop?.alignStart && (layout.desktop?.alignCenter || layout.desktop?.justifyRight),
  'mt-10': !layout.mobile?.alignCenter && !layout.mobile?.alignEnd,
  'lg:mt-10': !layout.desktop?.alignCenter && !layout.desktop?.alignEnd && !layout.desktop?.alignStart,
  'lg:mt-15': layout.desktop?.alignStart,
  'lg:ml-40': !layout.swapFlexItemOrder,
  'order-1 ml-0 <lg:px-5 lg:pl-20': layout.swapFlexItemOrder,
  'md:w-1/2 md:m-0 md:px-5': layout.desktop?.justifyRight,
  'lg:w-1/2': !layout.desktop?.imgAbsolute
})

const getFlexItemTwoClasses = (layout) => ({
  'full': true,
  '<lg:absolute-0': layout.mobile?.imgAbsolute,
  'lg:absolute-0': layout.desktop?.imgAbsolute,
  'lg:w-1/2': !layout.desktop?.imgAbsolute
})

const header = useHeaderStore()
const headerBounding = useElementBounding(() => header.refs.header)

const scrollContainerRef = ref<InstanceType<typeof BaseScrollType> | null>(null)
const itemRefs = ref([])

const activateNavItem = (i: number) => {
  let offset = 0
  if (i !== 0) offset = (headerBounding.width.value - 152) / 2

  apply({
    scaleX: rescaleProgress(sections[i].yearOffset),
    transition: {
      ease: [0.16, 1, 0.3, 1],
      duration: 600,
    }
  })

  if (headerBounding.width.value < 900 && document.querySelector(`#scroll-nav-${i}`)) {
    scrollToElement(`#scroll-nav-${i}`, {
      direction: 'left',
      isCustomSelector: true,
      offset
    })
  }
}

onMounted(() => {
  itemRefs.value.forEach((section, i) => {
    useIntersectionObserver(
      section,
      ([{ isIntersecting }]) => {
        if (isIntersecting)
          activateNavItem(i)
      },
      { threshold: 0.5 }
    )
  })
})

whenever(scrollContainerRef, (ref) => {
  const { directions, arrivedState, hasNext } = ref
  if (directions.top && !arrivedState.bottom && !hasNext)
    scroll({ top: 0, behavior: 'smooth' })
})
</script>
